import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create Investment
export const createInvestment = createAsyncThunk(
  'investments/create',
  async (formData, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${auth.token}`
        }
      };
      const response = await axios.post(`${API_URL}/investments`, formData, config);
      toast.success('Investment submitted! Awaiting verification.');
      return response.data.investment;
    } catch (error) {
      const message = error.response?.data?.message || 'Investment failed';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Get My Investments
export const getMyInvestments = createAsyncThunk(
  'investments/getMy',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const config = {
        headers: { Authorization: `Bearer ${auth.token}` }
      };
      const response = await axios.get(`${API_URL}/investments/my`, config);
      return response.data.investments;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

// Get All Investments (Admin)
export const getAllInvestments = createAsyncThunk(
  'investments/getAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const config = {
        headers: { Authorization: `Bearer ${auth.token}` }
      };
      const response = await axios.get(`${API_URL}/investments/all`, config);
      return response.data.investments;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

// Verify Investment
export const verifyInvestment = createAsyncThunk(
  'investments/verify',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const config = {
        headers: { Authorization: `Bearer ${auth.token}` }
      };
      const response = await axios.put(`${API_URL}/investments/verify/${id}`, {}, config);
      toast.success('Investment verified successfully');
      return response.data.investment;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification failed');
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

const investmentSlice = createSlice({
  name: 'investments',
  initialState: {
    investments: [],
    loading: false,
    error: null,
    totalInvested: 0,
    totalProfit: 0,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createInvestment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvestment.fulfilled, (state, action) => {
        state.loading = false;
        state.investments.unshift(action.payload);
      })
      .addCase(createInvestment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getMyInvestments.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMyInvestments.fulfilled, (state, action) => {
        state.loading = false;
        state.investments = action.payload;
        state.totalInvested = action.payload.reduce((sum, inv) => sum + inv.amount, 0);
        state.totalProfit = action.payload.reduce((sum, inv) => sum + inv.profitEarned, 0);
      })
      .addCase(getMyInvestments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAllInvestments.fulfilled, (state, action) => {
        state.investments = action.payload;
      })
      .addCase(verifyInvestment.fulfilled, (state, action) => {
        const index = state.investments.findIndex(i => i._id === action.payload._id);
        if (index !== -1) {
          state.investments[index] = action.payload;
        }
      });
  },
});

export const { clearError } = investmentSlice.actions;
export default investmentSlice.reducer;